import React, { useState, useEffect, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useCourseCatalogue, useProfileCourses } from '../../hooks';
import { AppDataContext } from '../../context';
import { isEligibleToEnrollV2, getEnabledCourse } from '../../utils';
import type { CourseCatalogueItem } from '../../types';
import { ArrowRightIcon } from '../../img/icons';
import CourseSkeleton from '../CourseSkeleton';

import messages from './messages';

type StatusMap = Record<string, boolean>;

const Catalogue: React.FC<{ username?: string }> = ({ username }) => {
  const [eligibilityStatus, setEligibilityStatus] = useState<StatusMap>({});
  const [completedStatus, setCompletedStatus] = useState<StatusMap>({});
  const [detailsLoaded, setDetailsLoaded] = useState<StatusMap>({});
  const [enabledCourses, setEnabledCourses] = useState<CourseCatalogueItem[]>([]);

  const intl = useIntl();
  const { authenticatedUser, allowedCourses } = useContext(AppDataContext);
  const [coursesData, courseDataLoaded] = useCourseCatalogue(allowedCourses);
  const { getCourseCompletions, getCourseGrade, isUserCoursesCompletionsLoaded } = useProfileCourses(authenticatedUser);
  const firstOfAllowedCourseId = allowedCourses[0]?.course_id;

  useEffect(() => {
    const fetchedEnabledCourses = getEnabledCourse(coursesData, allowedCourses, username);
    setEnabledCourses(fetchedEnabledCourses);
    fetchedEnabledCourses.forEach((courseInstance) => {
      const courseId = courseInstance.search?.data?.id;
      if (courseId) {
        const preCourseId = courseInstance?.metadata?.pre_requisite_courses?.[0] || '';
        const preCourseCompletion = getCourseCompletions(preCourseId);
        const preCourseGrade = getCourseGrade(preCourseId);
        isEligibleToEnrollV2(preCourseId, null, true, authenticatedUser, preCourseCompletion, preCourseGrade)
          .then((eligibleData: boolean | { eligible: boolean, completed: boolean }) => {
            if (typeof eligibleData !== 'boolean') {
              setEligibilityStatus((prev) => ({
                ...prev,
                [courseId]: courseId === firstOfAllowedCourseId ? true : eligibleData.eligible,
              }));
            }
            setDetailsLoaded((prev) => ({
              ...prev,
              [courseId]: true,
            }));
          });
        const courseCompletion = getCourseCompletions(courseId);
        const courseGrade = getCourseGrade(courseId);
        isEligibleToEnrollV2(courseId, null, true, authenticatedUser, courseCompletion, courseGrade)
          .then((eligibleData: boolean | { eligible: boolean, completed: boolean }) => {
            if (typeof eligibleData !== 'boolean') {
              setCompletedStatus((prev) => ({
                ...prev,
                [courseId]: eligibleData.completed,
              }));
            }
          });
      }
    });
  }, [coursesData, courseDataLoaded, username, isUserCoursesCompletionsLoaded]);

  const getEnrollmentLink = (courseId: string) => (
    <NavLink to={`/course-overview/${courseId}`} className="c_button">
      <ArrowRightIcon />
    </NavLink>
  );

  if (!courseDataLoaded) {
    return (
      <CourseSkeleton />
    );
  }

  return (
    <>
      {enabledCourses.map((courseInstance) => {
        const courseId = courseInstance?.search?.data?.id || '';

        return (
          <div
            className="course-item"
            data-id={courseId}
            key={courseId}
          >
            <div className="top-c">
              <img
                src={`${getConfig().LMS_BASE_URL}${courseInstance?.search?.data?.image_url}`}
                alt={
                courseInstance?.search?.data?.content?.display_name || intl.formatMessage(messages.courseImageAlt)
              }
              />
            </div>
            <div className="c-name">{courseInstance?.search?.data?.content?.display_name}</div>
            <p>
              {courseInstance?.metadata
                ? courseInstance?.metadata?.description || intl.formatMessage(messages.noDescription)
                : ''}
            </p>
            <div className="c-details">
              <span className="it">{intl.formatMessage(messages.timeLabel)}:</span>{' '}
              <span className="reg">{courseInstance?.metadata?.duration}</span>{' '}
              {detailsLoaded[courseId] && (
                <>
                  <span className="c_line" />
                  <span className="it">{intl.formatMessage(messages.accessLabel)}:</span>{' '}
                  {eligibilityStatus[courseId] ? (
                    <>
                      <span id="access" className="reg">
                        {intl.formatMessage(completedStatus[courseId] ? messages.complete : messages.availableNow)}
                      </span>
                      {getEnrollmentLink(courseId)}
                    </>
                  ) : (
                    <>
                      <span id="access" className="reg">
                        {intl.formatMessage(messages.completePreviousModule)}
                      </span>
                      {getEnrollmentLink(courseId)}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default Catalogue;
